import * as React from 'react'
import { isTabularServiceFilledColumn } from '@atbash-labs/ontology-core'
import {
  useSaveTabularIngestPageRowsMutation,
  type TabularIngestJobPage,
  type TabularIngestJobPageRow,
  type TabularPageEditIssue,
  type TabularPageTableEditsInput
} from '@axilon-apps/domains-ontology-ui-state'
import { extractErrorMessage } from '../IngestPage/util/extractErrorMessage'

/** One table's edits on the page being edited, whole: a save sends all of them. Values are CSV text, '' is null. */
export interface TableEditDraft {
  /** Stored row position → column → value. */
  changed: Record<number, Record<string, string>>
  deleted: Record<number, true>
  added: Array<{ id: string; values: Record<string, string> }>
}

export interface PageEditDraft {
  pageId: string
  /** The page as a reader names it, for the prompt before leaving it. */
  label: string
  /** The page's edit revision the draft started from. */
  revision: number
  tables: Record<string, TableEditDraft>
}

/** The page's saved edits, as the draft an edit starts from. */
export function savedPageDraft(
  pageId: string,
  label: string,
  detail: Pick<TabularIngestJobPage, 'tables' | 'edits'>
): PageEditDraft {
  const tables: Record<string, TableEditDraft> = {}
  for (const table of detail.tables) {
    if (table.unavailable) continue
    const draft: TableEditDraft = { changed: {}, deleted: {}, added: [] }
    for (const row of table.rows) {
      for (const column of Object.keys(row.changes ?? {})) {
        const at = table.columns.indexOf(column)
        if (at >= 0) (draft.changed[row.position] ??= {})[column] = row.cells[at] ?? ''
      }
      if (row.deleted) draft.deleted[row.position] = true
    }
    draft.added = (table.added ?? []).map((row) => ({
      id: row.id,
      values: Object.fromEntries(
        table.columns.flatMap((column, at) => {
          const value = row.cells[at] ?? ''
          return !isTabularServiceFilledColumn(column) && value !== '' ? [[column, value]] : []
        })
      )
    }))
    tables[table.table] = draft
  }
  return { pageId, label, revision: detail.edits?.revision ?? 0, tables }
}

function updateTable(
  draft: PageEditDraft,
  table: string,
  change: (edits: TableEditDraft) => TableEditDraft
): PageEditDraft {
  const edits = draft.tables[table] ?? { changed: {}, deleted: {}, added: [] }
  return { ...draft, tables: { ...draft.tables, [table]: change(edits) } }
}

/** Set a stored row's cell. A value equal to what the run read is no change. */
export function setStoredCell(
  draft: PageEditDraft,
  table: string,
  columns: readonly string[],
  row: Pick<TabularIngestJobPageRow, 'position' | 'cells' | 'changes'>,
  column: string,
  value: string
): PageEditDraft {
  const original = row.changes?.[column]?.original ?? row.cells[columns.indexOf(column)] ?? ''
  return updateTable(draft, table, (edits) => {
    const cells = { ...edits.changed[row.position] }
    if (value === original) delete cells[column]
    else cells[column] = value
    const changed = { ...edits.changed }
    if (Object.keys(cells).length > 0) changed[row.position] = cells
    else delete changed[row.position]
    return { ...edits, changed }
  })
}

export function toggleDeletedRow(
  draft: PageEditDraft,
  table: string,
  position: number
): PageEditDraft {
  return updateTable(draft, table, (edits) => {
    const deleted = { ...edits.deleted }
    if (deleted[position]) delete deleted[position]
    else deleted[position] = true
    return { ...edits, deleted }
  })
}

export function addDraftRow(draft: PageEditDraft, table: string): PageEditDraft {
  return updateTable(draft, table, (edits) => {
    const taken = edits.added.map((row) => Number(/^new-(\d+)$/.exec(row.id)?.[1] ?? 0))
    const id = `new-${Math.max(0, ...taken) + 1}`
    return { ...edits, added: [...edits.added, { id, values: {} }] }
  })
}

export function setAddedCell(
  draft: PageEditDraft,
  table: string,
  id: string,
  column: string,
  value: string
): PageEditDraft {
  return updateTable(draft, table, (edits) => ({
    ...edits,
    added: edits.added.map((row) => {
      if (row.id !== id) return row
      const values = { ...row.values }
      if (value === '') delete values[column]
      else values[column] = value
      return { ...row, values }
    })
  }))
}

export function removeAddedRow(draft: PageEditDraft, table: string, id: string): PageEditDraft {
  return updateTable(draft, table, (edits) => ({
    ...edits,
    added: edits.added.filter((row) => row.id !== id)
  }))
}

function hasEdits(edits: TableEditDraft | undefined): boolean {
  return (
    edits !== undefined &&
    (Object.keys(edits.changed).length > 0 ||
      Object.keys(edits.deleted).length > 0 ||
      edits.added.length > 0)
  )
}

/** The save's body: each table the draft or the saved edits touch. */
export function pageEditsInput(
  draft: PageEditDraft,
  saved: PageEditDraft
): Record<string, TabularPageTableEditsInput> {
  const tables: Record<string, TabularPageTableEditsInput> = {}
  for (const [table, edits] of Object.entries(draft.tables)) {
    if (!hasEdits(edits) && !hasEdits(saved.tables[table])) continue
    tables[table] = {
      changed: Object.fromEntries(
        Object.entries(edits.changed).map(([position, cells]) => [position, { ...cells }])
      ),
      deleted: Object.keys(edits.deleted).map(Number),
      added: edits.added.map((row) => ({ id: row.id, values: { ...row.values } }))
    }
  }
  return tables
}

/** A draft as text that two drafts with the same edits share, whatever order they were made in. */
function canonical(draft: PageEditDraft): string {
  const sorted = (record: Record<string, unknown>) =>
    Object.keys(record)
      .sort()
      .map((key) => [key, record[key]])
  return JSON.stringify(
    Object.keys(draft.tables)
      .sort()
      .flatMap((table) => {
        const edits = draft.tables[table]
        if (!hasEdits(edits) || !edits) return []
        return [
          [
            table,
            sorted(edits.changed).map(([position, cells]) => [
              position,
              sorted(cells as Record<string, string>)
            ]),
            Object.keys(edits.deleted).sort(),
            edits.added.map((row) => [row.id, sorted(row.values)])
          ]
        ]
      })
  )
}

export function sameEdits(a: PageEditDraft, b: PageEditDraft): boolean {
  return canonical(a) === canonical(b)
}

/** The page being edited and what the reader may do with its draft. */
export interface RunPageEdits {
  draft?: PageEditDraft
  /** The draft has edits the page has not saved. */
  dirty: boolean
  saving: boolean
  error?: string
  /** The cells and rows the last save refused. */
  issues: TabularPageEditIssue[]
  start: (draft: PageEditDraft) => void
  update: (change: (draft: PageEditDraft) => PageEditDraft) => void
  /** Leave edit mode, dropping the draft. */
  stop: () => void
  /** Save the draft. Resolves false when it was refused. */
  save: () => Promise<boolean>
  /** Before showing another page: asks first when the draft is unsaved. True when the reader may go. */
  confirmLeave: () => boolean
}

interface Editing {
  saved: PageEditDraft
  draft: PageEditDraft
}

export function useRunPageEdits(jobId: string | undefined): RunPageEdits {
  const [saveRows] = useSaveTabularIngestPageRowsMutation()
  // The ref answers callers in the same tick as a save, before React re-renders.
  const editingRef = React.useRef<Editing | undefined>(undefined)
  const [editing, setEditingState] = React.useState<Editing>()
  const setEditing = React.useCallback((next: Editing | undefined) => {
    editingRef.current = next
    setEditingState(next)
  }, [])
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string>()
  const [issues, setIssues] = React.useState<TabularPageEditIssue[]>([])
  const stop = React.useCallback(() => {
    setEditing(undefined)
    setError(undefined)
    setIssues([])
  }, [setEditing])
  React.useEffect(stop, [jobId, stop])
  const dirty = editing !== undefined && !sameEdits(editing.draft, editing.saved)
  React.useEffect(() => {
    if (!dirty) return
    const onUnload = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [dirty])

  const start = React.useCallback(
    (draft: PageEditDraft) => {
      setEditing({ saved: draft, draft })
      setError(undefined)
      setIssues([])
    },
    [setEditing]
  )
  const update = React.useCallback(
    (change: (draft: PageEditDraft) => PageEditDraft) => {
      const current = editingRef.current
      if (current) setEditing({ ...current, draft: change(current.draft) })
    },
    [setEditing]
  )
  const save = React.useCallback(async () => {
    const current = editingRef.current
    if (!jobId || !current) return false
    setSaving(true)
    setError(undefined)
    setIssues([])
    try {
      await saveRows({
        jobId,
        pageId: current.draft.pageId,
        revision: current.saved.revision,
        tables: pageEditsInput(current.draft, current.saved)
      }).unwrap()
      setEditing(undefined)
      return true
    } catch (caught) {
      setError(extractErrorMessage(caught, 'The edits could not be saved.'))
      setIssues(refusedIssues(caught))
      return false
    } finally {
      setSaving(false)
    }
  }, [jobId, saveRows, setEditing])
  const confirmLeave = React.useCallback(() => {
    const current = editingRef.current
    if (!current) return true
    const unsaved = !sameEdits(current.draft, current.saved)
    if (unsaved && !window.confirm(`Discard unsaved edits on ${current.draft.label}?`)) return false
    stop()
    return true
  }, [stop])
  return {
    ...(editing ? { draft: editing.draft } : {}),
    dirty,
    saving,
    ...(error ? { error } : {}),
    issues,
    start,
    update,
    stop,
    save,
    confirmLeave
  }
}

function refusedIssues(error: unknown): TabularPageEditIssue[] {
  const issues = (error as { data?: { issues?: unknown } } | undefined)?.data?.issues
  return Array.isArray(issues) ? (issues as TabularPageEditIssue[]) : []
}
